import React, { useEffect, useMemo, useRef, useState } from 'react';
import ListContent from '../composites/ListContent';
import EmptyState from '../composites/EmptyState';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import GroupComplianceRow from './GroupComplianceRow';
import VelocityPie from '../Clients/VelocityPie';
import { useUserAction } from '../../providers';
import { calculateOverallStatistics } from '../../utils';
import styles from './GroupMembersView.module.scss';
import userWorkoutsRowStyles from '../Clients/UserWorkoutsRow.module.scss';
import { locales } from '../../constants/locale';

import type { Member, SessionStatistic, UserStatistics } from '../../types';
import { UserType } from '../../constants';
import type { TrainerDashboardData } from '../../appState/appInitialState';

interface GroupComplianceListProps {
  members: Member[];
  trainers: TrainerDashboardData[];
  userType: UserType;
  onEditClient: (member: Member) => void;
  onRemoveMember: (member: Member) => void;
  onGoToInDepthUserPage: (member: Member) => void;
  sectionTitle?: string;
  showOverallMetrics?: boolean;
  isActive?: boolean;
  onOverallStatisticsChange?: (stats: UserStatistics | null) => void;
}

const texts = locales.en.components.clients.clientsContent;

const GroupComplianceList: React.FC<GroupComplianceListProps> = ({
  members,
  trainers,
  userType,
  onEditClient,
  onRemoveMember,
  onGoToInDepthUserPage,
  sectionTitle,
  showOverallMetrics = true,
  isActive = true,
  onOverallStatisticsChange,
}) => {
  const { getSessionStatistics } = useUserAction();
  const [memberSessionStatistics, setMemberSessionStatistics] = useState<
    Record<string, SessionStatistic[]>
  >({});
  const lastFetchedStatsKeyRef = useRef<string | null>(null);
  const inFlightStatsKeyRef = useRef<string | null>(null);
  const memberIds = useMemo(
    () =>
      members
        .map(({ id }) => id)
        .filter(Boolean)
        .sort(),
    [members],
  );
  const memberIdsKey = memberIds.join('|');

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!memberIds.length) {
      setMemberSessionStatistics({});
      lastFetchedStatsKeyRef.current = null;
      inFlightStatsKeyRef.current = null;
      onOverallStatisticsChange?.(null);
      return;
    }
    if (
      memberIdsKey === lastFetchedStatsKeyRef.current ||
      memberIdsKey === inFlightStatsKeyRef.current
    ) {
      return;
    }
    const currentFetchKey = memberIdsKey;
    inFlightStatsKeyRef.current = currentFetchKey;
    const fetchStatistics = async () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      try {
        const stats = await getSessionStatistics(
          memberIds,
          '2024-01-01T00:00:00.000Z',
          tomorrow.toISOString(),
          true,
        );
        setMemberSessionStatistics(stats);
        lastFetchedStatsKeyRef.current = memberIdsKey;
      } catch {
        setMemberSessionStatistics({});
      } finally {
        if (inFlightStatsKeyRef.current === currentFetchKey) {
          inFlightStatsKeyRef.current = null;
        }
      }
    };
    fetchStatistics();
  }, []);

  const trainerLookup = useMemo(() => {
    const lookup: Record<string, TrainerDashboardData> = {};
    trainers.forEach((trainer) => {
      lookup[trainer.trainer.id] = trainer;
    });
    return lookup;
  }, [trainers]);

  const statsSummary = useMemo(() => {
    const lastWorkoutMap: Record<string, SessionStatistic | null> = {};
    const last30DayMap: Record<string, SessionStatistic[]> = {};
    const allTimeMap: Record<string, SessionStatistic[]> = {};
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    Object.entries(memberSessionStatistics).forEach(
      ([memberId, stats = []]) => {
        const sortedStats = [...stats].sort(
          (a, b) =>
            new Date(b.createdDate).getTime() -
            new Date(a.createdDate).getTime(),
        );
        lastWorkoutMap[memberId] = sortedStats[0] ?? null;
        last30DayMap[memberId] = sortedStats.filter(
          (stat) => new Date(stat.createdDate) >= thirtyDaysAgo,
        );
        allTimeMap[memberId] = stats;
      },
    );

    const overall = calculateOverallStatistics(
      Object.values(lastWorkoutMap).filter((stat): stat is SessionStatistic =>
        Boolean(stat),
      ),
      [],
      Object.values(last30DayMap).flat(),
      Object.values(allTimeMap).flat(),
    );

    return {
      lastWorkoutMap,
      last30DayMap,
      allTimeMap,
      overall,
    };
  }, [memberSessionStatistics]);

  useEffect(() => {
    onOverallStatisticsChange?.(statsSummary.overall);
  }, [statsSummary.overall, onOverallStatisticsChange]);

  const renderVelocityPies = () => {
    const {
      lastWorkoutConcentricGoodRepsPercentage,
      lastWorkoutConcentricFastRepsPercentage,
      lastWorkoutConcentricSlowRepsPercentage,
      lastMonthConcentricGoodRepsPercentage,
      lastMonthConcentricFastRepsPercentage,
      lastMonthConcentricSlowRepsPercentage,
      allTimeConcentricGoodRepsPercentage,
      allTimeConcentricFastRepsPercentage,
      allTimeConcentricSlowRepsPercentage,
    } = statsSummary.overall || {};

    const renderPieOrDash = (
      values: {
        good?: number;
        fast?: number;
        slow?: number;
        missed?: number;
      },
      subtitle: string,
    ) => {
      if (!values.good && !values.fast && !values.slow) {
        return (
          <div className={styles.velocityPieContainer}>
            <span className={styles.emptyStat}>-</span>
          </div>
        );
      }
      return (
        <div className={styles.velocityPieContainer}>
          <VelocityPie
            good={values.good}
            fast={values.fast}
            slow={values.slow}
            missed={values.missed}
            subtitle={subtitle}
          />
        </div>
      );
    };

    return (
      <div className={styles.velocityPiesContainer}>
        {renderPieOrDash(
          {
            good: lastWorkoutConcentricGoodRepsPercentage,
            fast: lastWorkoutConcentricFastRepsPercentage,
            slow: lastWorkoutConcentricSlowRepsPercentage,
            missed:
              statsSummary.overall?.lastWorkoutConcentricMissedRepsPercentage,
          },
          texts.lastWorkout,
        )}
        {renderPieOrDash(
          {
            good: lastMonthConcentricGoodRepsPercentage,
            fast: lastMonthConcentricFastRepsPercentage,
            slow: lastMonthConcentricSlowRepsPercentage,
            missed:
              statsSummary.overall?.lastMonthConcentricMissedRepsPercentage,
          },
          texts.last30Days,
        )}
        {renderPieOrDash(
          {
            good: allTimeConcentricGoodRepsPercentage,
            fast: allTimeConcentricFastRepsPercentage,
            slow: allTimeConcentricSlowRepsPercentage,
            missed: statsSummary.overall?.allTimeConcentricMissedRepsPercentage,
          },
          'All Time',
        )}
      </div>
    );
  };

  return (
    <div className={styles.contentContainer}>
      {showOverallMetrics && (
        <div className={styles.contentContainer}>
          <span className={styles.sectionTitle}>{texts.OVERALL_METRICS}</span>
          {renderVelocityPies()}
        </div>
      )}
      {sectionTitle && (
        <span className={styles.sectionTitle}>{sectionTitle}</span>
      )}
      <ListContent
        items={members}
        titles={[
          'Athlete Name',
          'LAST WORKOUT',
          'LAST MONTH',
          'ALL TIME',
          'Options',
        ]}
        listHeaderClassName={userWorkoutsRowStyles.workoutsListGrid}
        ItemComponent={({ data }) => (
          <GroupComplianceRow
            member={data}
            trainer={
              trainerLookup[
                data.trainerIds.find((id) => Boolean(trainerLookup[id])) ?? ''
              ]?.trainer
            }
            userType={userType}
            openEditClientModal={() => onEditClient(data)}
            onRemoveMember={() => onRemoveMember(data)}
            onGoToInDepthUserPage={() => onGoToInDepthUserPage(data)}
            lastWorkoutSessionStatistic={
              statsSummary.lastWorkoutMap[data.id] ?? null
            }
            last30DaysSessionStatistics={
              statsSummary.last30DayMap[data.id] ?? []
            }
            allTimeSessionStatistics={statsSummary.allTimeMap[data.id] ?? []}
          />
        )}
        EmptyStateComponent={() => (
          <EmptyState
            icon={<AccountCircleRoundedIcon />}
            text="No Members in this Group"
          />
        )}
      />
    </div>
  );
};

export default GroupComplianceList;
